import React from 'react'

import {
  Button,
  Separator,
  Modal,
  ModalInjectedProps,
} from '@sketch/components'

import { useToast } from '@sketch/toasts'

import { useMoveShareToProject } from 'modules/projects/operations'
import { ProjectFragment, ShareListItemFragment } from '@sketch/gql-types'
import ShareAccessList from 'modules/shares/components/ShareAccessList'

import {
  Bold,
  CenterIconText,
  FolderIcon,
  StyledBanner,
} from './ConfirmMoveModal.styles'

interface ConfirmMoveModalProps extends ModalInjectedProps {
  newProject: ProjectFragment
  share: ShareListItemFragment
  isDraftDocument?: boolean
}

function ConfirmMoveModal(props: ConfirmMoveModalProps) {
  const { hideModal, newProject, share, isDraftDocument } = props
  const { showToast } = useToast()

  const [moveShare, { loading }] = useMoveShareToProject({
    onCompleted: project => {
      showToast(`Document moved to "${project.name}"`)
      hideModal()
    },
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  return (
    <Modal
      title="Move Document?"
      onCancel={hideModal}
      data-testid="confirm-move-modal"
    >
      <Modal.Body>
        <CenterIconText>
          to <FolderIcon />{' '}
          <Bold>
            <span data-testid="project-name">{newProject.name}</span>
          </Bold>
        </CenterIconText>
        {isDraftDocument && (
          <>
            <StyledBanner type="warning" showIcon={false}>
              Documents cannot be moved back to My Drafts.
            </StyledBanner>
            <Separator mb={6} />
          </>
        )}
        <ShareAccessList
          shareIdentifier={share.identifier}
          destination={{ project: newProject }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          loading={loading}
          onClick={() =>
            moveShare({
              shareId: share.identifier,
              projectId: newProject.identifier,
            })
          }
          variant="primary"
        >
          Move Document
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export { ConfirmMoveModal }
